import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Calendar, MapPin, MessageSquare, CheckCircle, ExternalLink, FileText, Clock, AlertCircle, Globe, Tag } from 'lucide-react';
import { formatShortDate, getCustomerTimezone, formatBookingDateWithVendorTime } from '../../utils/dateFormatter';
import { Link } from 'react-router-dom';
import VendorAvatar from '../VendorAvatar';
import { isBookingCancelled } from '../../utils/bookingCancellation';

// Small timezone notice for customer-facing time displays
const CustomerTimezoneNotice = () => {
  const tz = getCustomerTimezone();
  const tzLabel = tz.split('/').pop().replace(/_/g, ' ');
  return (
    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
      <Globe className="h-2.5 w-2.5" />
      Your time ({tzLabel})
    </span>
  );
};

const InquiryCard = ({
  inquiry,
  bookings,
  disputes,
  handleOpenMessaging,
  setDisputingInquiry,
  navigate,
  inquiries,
  setSelectedBooking,
  setShowPaymentCheckout,
  setShowServiceAgreement,
  onBookingCancelled,
  onReviewClick,
  reviews = []
}) => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const getBookingForInquiry = (inquiryId) => {
    return bookings.find(b => b.inquiry_id === inquiryId);
  };

  const booking = getBookingForInquiry(inquiry.id);
  const hasUnreadMessages = inquiry.unread_count > 0;
  
  // Use server-provided payment window status (single source of truth)
  const getPaymentWindowInfo = () => {
    if (!booking) return null;
    
    // Server provides: payment_window_status, can_pay_deposit, time_remaining_seconds
    const windowStatus = booking.payment_window_status;
    const canPay = booking.can_pay_deposit;
    const timeRemaining = booking.time_remaining_seconds;
    
    // If already paid, no need to show countdown
    if (windowStatus === 'paid' || booking.deposit_paid) {
      return { expired: false, paid: true, canPay: false, text: null, colorClass: null };
    }
    
    // If expired by server
    if (windowStatus === 'expired' || !canPay) {
      return { expired: true, paid: false, canPay: false, text: 'Expired', colorClass: 'text-red-600' };
    }
    
    // Calculate display from time_remaining_seconds (fallback to client calculation if not provided)
    if (timeRemaining != null && timeRemaining > 0) {
      const hours = Math.floor(timeRemaining / 3600);
      const minutes = Math.floor((timeRemaining % 3600) / 60);
      
      // Color states: green >48h, amber 48-6h, red <6h
      let colorClass = 'text-emerald-600';
      if (hours <= 48 && hours > 6) {
        colorClass = 'text-amber-600';
      } else if (hours <= 6) {
        colorClass = 'text-red-600';
      }
      
      let text;
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        text = `${days}d ${hours % 24}h left`;
      } else if (hours > 0) {
        text = `${hours}h ${minutes}m left`;
      } else {
        text = `${minutes}m left`;
      }
      
      return { expired: false, paid: false, canPay: true, text, colorClass };
    }
    
    // Fallback: assume open if no time data (for backwards compatibility)
    return { expired: false, paid: false, canPay: true, text: null, colorClass: null };
  };

  // Determine current status and what action is needed
  const getStatusInfo = () => {
    const status = inquiry.status;
    const paymentWindow = getPaymentWindowInfo();

    // Centralized cancellation guard: if the underlying booking is cancelled
    // but the inquiry row hasn't refreshed yet (30s polling lag), still show
    // the cancelled label so the customer is never told "Confirmed" for a
    // cancelled booking.
    const cancelledBooking = Array.isArray(bookings)
      ? bookings.find((b) => b.inquiry_id === inquiry.id && isBookingCancelled(b))
      : null;
    if (status === 'cancelled' || cancelledBooking) {
      const byVendor = (cancelledBooking?.status === 'cancelled_by_vendor') || (cancelledBooking?.cancelled_by === 'vendor');
      return {
        label: 'Cancelled',
        sublabel: byVendor ? 'Cancelled by provider' : 'This booking was cancelled',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
      };
    }

    if (status === 'pending') {
      return { 
        label: 'Waiting on Provider', 
        sublabel: 'Providers usually respond within 24 hours',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50/70'
      };
    }
    if (status === 'reviewing') {
      return { 
        label: 'Discussing Scope', 
        sublabel: 'Provider is reviewing your request',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50/70'
      };
    }
    if (status === 'proposal_sent') {
      return { 
        label: 'Proposal Ready', 
        sublabel: 'Review and accept to continue',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50/70',
        actionRequired: true
      };
    }
    if (status === 'proposal_accepted' || status === 'accepted') {
      // If booking doesn't exist yet, show processing state
      if (!booking) {
        return { 
          label: 'Processing', 
          sublabel: 'Setting up your booking...',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50/70'
        };
      }
      if (!booking.service_agreement_accepted_by_customer) {
        return { 
          label: 'Sign Agreement', 
          sublabel: 'Review and sign to proceed',
          color: 'text-amber-600',
          bgColor: 'bg-amber-50/70',
          actionRequired: true
        };
      }
      if (!booking.deposit_paid) {
        // Check payment window status from server
        if (paymentWindow?.expired) {
          return { 
            label: 'Payment Window Expired', 
            sublabel: 'This date is no longer held. Message the provider to request a new booking.',
            color: 'text-gray-600',
            bgColor: 'bg-gray-100',
            actionRequired: false,
            expired: true
          };
        }
        return { 
          label: 'Payment Required', 
          sublabel: 'Complete payment to secure this booking',
          color: 'text-gray-700',
          bgColor: 'bg-amber-50/50',
          actionRequired: true,
          showClock: paymentWindow?.canPay && paymentWindow?.text,
          paymentWindow
        };
      }
      return { 
        label: 'Confirmed', 
        sublabel: 'Booking confirmed',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50/70'
      };
    }
    if (status === 'agreement_signed') {
      if (!booking) {
        return { 
          label: 'Processing', 
          sublabel: 'Loading booking...',
          color: 'text-blue-600',
          bgColor: 'bg-blue-50/70'
        };
      }
      if (!booking.deposit_paid) {
        // Check payment window status from server
        if (paymentWindow?.expired) {
          return { 
            label: 'Payment Window Expired', 
            sublabel: 'This date is no longer held. Message the provider to request a new booking.',
            color: 'text-gray-600',
            bgColor: 'bg-gray-100',
            actionRequired: false,
            expired: true
          };
        }
        return { 
          label: 'Payment Required', 
          sublabel: 'Complete payment to secure this booking',
          color: 'text-gray-700',
          bgColor: 'bg-amber-50/50',
          actionRequired: true,
          showClock: paymentWindow?.canPay && paymentWindow?.text,
          paymentWindow
        };
      }
    }
    if (status === 'deposit_paid') {
      // Check if this is actually fully paid (from full payment option)
      if (booking && booking.deposit_paid && booking.final_paid) {
        return { 
          label: 'Paid in Full', 
          sublabel: 'No remaining payments',
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50/70'
        };
      }
      return { 
        label: 'Deposit Paid', 
        sublabel: 'Booking secured',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50/70'
      };
    }
    if (status === 'fully_paid') {
      return { 
        label: 'Paid in Full', 
        sublabel: 'No remaining payments',
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-50/70'
      };
    }
    if (status === 'in_progress') {
      return { 
        label: 'In Progress', 
        sublabel: 'Service is underway',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50/70'
      };
    }
    if (status === 'completed_by_vendor') {
      return { 
        label: 'Action Required', 
        sublabel: 'Confirm completion and pay final amount',
        color: 'text-amber-600',
        bgColor: 'bg-amber-50/70',
        actionRequired: true
      };
    }
    if (status === 'final_paid' || status === 'completed') {
      return { 
        label: 'Completed', 
        sublabel: 'Booking completed',
        color: 'text-gray-600',
        bgColor: 'bg-gray-50'
      };
    }
    if (status === 'cancelled') {
      return { 
        label: 'Cancelled', 
        sublabel: inquiry.cancellation_reason || 'This booking was cancelled',
        color: 'text-gray-500',
        bgColor: 'bg-gray-50'
      };
    }
    if (status === 'on_hold') {
      return { 
        label: 'On Hold', 
        sublabel: inquiry.on_hold_note || 'Temporarily paused',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50/70'
      };
    }
    return { 
      label: 'Request Sent', 
      sublabel: 'Waiting for provider response',
      color: 'text-gray-500',
      bgColor: 'bg-gray-50'
    };
  };

  // Get the single primary action for current state
  const getPrimaryAction = () => {
    const status = inquiry.status;
    const paymentWindow = getPaymentWindowInfo();
    
    // Priority: Payment actions take precedence over messages when deposit is due
    // For accepted proposals with booking - determine next step first
    if (['proposal_accepted', 'accepted', 'agreement_signed', 'deposit_deadline_active'].includes(status) && booking) {
      // Step 1: If agreement not signed by customer, show "Sign Agreement"
      if (!booking.service_agreement_accepted_by_customer) {
        return {
          label: 'Sign Agreement',
          onClick: () => {
            setSelectedBooking(booking);
            setShowServiceAgreement(true);
          },
          variant: 'default',
          icon: FileText,
          pulse: true
        };
      }
      
      // Step 2: If agreement signed but deposit not paid
      if (!booking.deposit_paid) {
        // If expired (server-enforced), show "Message Provider" as primary action
        if (paymentWindow?.expired || !paymentWindow?.canPay) {
          return {
            label: 'Message Provider',
            onClick: () => handleOpenMessaging(inquiry),
            variant: 'default',
            icon: MessageSquare,
            pulse: false,
            expired: true
          };
        }
        return {
          label: 'Complete Payment',
          onClick: () => {
            setSelectedBooking(booking);
            setShowPaymentCheckout(true);
          },
          variant: 'default',
          icon: null,
          pulse: true
        };
      }
    }
    
    // Show unread messages badge only when there's no payment action pending
    if (hasUnreadMessages) {
      return {
        label: `${inquiry.unread_count} New Message${inquiry.unread_count > 1 ? 's' : ''}`,
        onClick: () => handleOpenMessaging(inquiry),
        variant: 'outline',
        icon: MessageSquare,
        pulse: false
      };
    }
    
    if (status === 'proposal_sent') {
      return {
        label: 'Review Proposal',
        onClick: () => handleOpenMessaging(inquiry),
        variant: 'default',
        icon: FileText
      };
    }
    
    if (status === 'completed_by_vendor' && booking && !booking.final_paid) {
      return {
        label: 'Pay Final Amount',
        onClick: () => {
          setSelectedBooking(booking);
          setShowPaymentCheckout(true);
        },
        variant: 'default',
        icon: null
      };
    }
    
    // Default: View Conversation (secondary style)
    if (!['cancelled', 'completed', 'final_paid'].includes(status)) {
      return {
        label: 'View Conversation',
        onClick: () => handleOpenMessaging(inquiry),
        variant: 'outline',
        icon: MessageSquare,
        secondary: true
      };
    }
    
    return null;
  };

  const statusInfo = getStatusInfo();
  const primaryAction = getPrimaryAction();

  // Determine card border color - only for action required states
  const getBorderColor = () => {
    if (statusInfo.expired) return 'border-l-gray-300';
    if (statusInfo.actionRequired) return 'border-l-amber-400';
    if (hasUnreadMessages) return 'border-l-blue-400';
    if (['completed', 'final_paid', 'cancelled'].includes(inquiry.status)) return 'border-l-gray-300';
    return 'border-l-emerald-400';
  };

  return (
    <Card className={`border-l-4 ${getBorderColor()}`}>
      <CardContent className="pt-5 pb-4 px-4">
        
        {/* 1. STATUS SUMMARY (Top - Visually Dominant) */}
        <div className={`rounded-md px-3 py-2 mb-4 ${statusInfo.bgColor}`}>
          <div className="flex items-center justify-between">
            <p className={`text-sm font-medium ${statusInfo.color}`}>
              {statusInfo.label}
            </p>
            {/* Countdown - single source of truth, with color states */}
            {statusInfo.showClock && statusInfo.paymentWindow && statusInfo.paymentWindow.text && (
              <span className={`flex items-center text-xs font-medium ${statusInfo.paymentWindow.colorClass}`}>
                <Clock className="h-3 w-3 mr-1" />
                {statusInfo.paymentWindow.text}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">{statusInfo.sublabel}</p>
        </div>

        {/* Provider Name + Event Info (Compact Header) */}
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <Link 
              to={`/vendors/${inquiry.vendor_id}`}
              className="group inline-flex items-center gap-2 hover:text-emerald-600"
            >
              <VendorAvatar
                vendor={inquiry}
                size={32}
                testId="inquiry-card-vendor-avatar"
              />
              <span className="font-semibold text-gray-900 group-hover:text-emerald-600 truncate">
                {inquiry.vendor_name || 'Service Provider'}
              </span>
              <ExternalLink className="h-3 w-3 text-gray-400 group-hover:text-emerald-500 flex-shrink-0" />
            </Link>
            <div className="flex flex-col gap-1 mt-1">
              <div className="flex items-center text-xs text-gray-500 gap-3">
                <span className="flex items-center">
                  <Calendar className="h-3 w-3 mr-1" />
                  {inquiry.event_type} · {formatShortDate(inquiry.event_date)}
                </span>
                <span className="flex items-center">
                  <MapPin className="h-3 w-3 mr-1" />
                  {inquiry.location}
                </span>
              </div>
              {/* Timezone notice for confirmed bookings */}
              {booking && booking.deposit_paid && <CustomerTimezoneNotice />}
              {inquiry.selected_package && (
                <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="customer-inquiry-selected-package">
                  {inquiry.selected_package.split(', ').map((name, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 rounded-md px-1.5 py-0.5">
                      <Tag className="h-3 w-3" /> {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 2. SERVICE AGREEMENT (Single line when signed, clarify if expired) */}
        {booking && (
          <div className="mb-3">
            {booking.service_agreement_accepted_by_customer && booking.service_agreement_accepted_by_vendor ? (
              <button 
                onClick={() => {
                  setSelectedBooking(booking);
                  setShowServiceAgreement(true);
                }}
                className="flex items-center text-xs text-gray-500 hover:text-gray-700"
              >
                <CheckCircle className="h-3 w-3 mr-1.5 text-gray-400" />
                {/* Show different text if payment window expired */}
                {statusInfo.expired 
                  ? 'Agreement signed (expired — deposit not received)'
                  : !booking.deposit_paid 
                    ? 'Agreement signed (payment pending)'
                    : 'Service Agreement Signed'
                }
              </button>
            ) : booking.service_agreement_accepted_by_customer ? (
              <p className="text-xs text-gray-500">Agreement: Provider reviewing</p>
            ) : ['proposal_accepted', 'accepted'].includes(inquiry.status) ? (
              <p className="text-xs text-amber-600 font-medium">Agreement: Ready to sign</p>
            ) : null}
          </div>
        )}

        {/* 3. PAYMENTS (Stacked: Deposit then Final) */}
        {booking && inquiry.agreed_price && (
          <div className="mb-4 space-y-1.5">
            {/* Check if paid in full (both deposit and final marked as paid) */}
            {booking.deposit_paid && booking.final_paid ? (
              <div className="flex items-center justify-between text-sm">
                <span className="text-emerald-600 font-medium">Paid in Full</span>
                <span className="text-emerald-600 font-semibold">
                  ${inquiry.agreed_price} ✓
                </span>
              </div>
            ) : (
              <>
                {/* Deposit */}
                <div className="flex items-center justify-between text-sm">
                  <span className={booking.deposit_paid ? 'text-gray-400' : 'text-gray-700'}>
                    Deposit (15%)
                  </span>
                  <span className={booking.deposit_paid ? 'text-gray-400' : 'font-medium text-gray-900'}>
                    ${booking.deposit_amount?.toFixed(2)}
                    {booking.deposit_paid && <span className="ml-1 text-xs">✓</span>}
                  </span>
                </div>
                
                {/* Final Payment */}
                <div className="flex items-center justify-between text-sm">
                  <span className={booking.final_paid ? 'text-gray-400' : booking.deposit_paid ? 'text-gray-700' : 'text-gray-400'}>
                    Final Payment
                  </span>
                  <span className={booking.final_paid ? 'text-gray-400' : booking.deposit_paid ? 'font-medium text-gray-900' : 'text-gray-400'}>
                    ${booking.final_amount?.toFixed(2)}
                    {booking.final_paid && <span className="ml-1 text-xs">✓</span>}
                  </span>
                </div>

                {/* Total - only show if not completed */}
                {!booking.final_paid && (
                  <div className="flex items-center justify-between text-sm pt-1.5 border-t border-gray-100">
                    <span className="text-gray-500">Total</span>
                    <span className="font-semibold text-gray-900">${inquiry.agreed_price}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* 4. PRIMARY ACTION (Bottom - Single CTA) */}
        {primaryAction && (
          <div className="mt-4">
            <Button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              variant={primaryAction.variant}
              className={`w-full ${
                primaryAction.disabled
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : primaryAction.secondary 
                    ? 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' 
                    : primaryAction.variant === 'destructive'
                      ? 'bg-red-600 hover:bg-red-700'
                      : primaryAction.variant === 'outline'
                        ? ''
                        : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
              data-testid={`inquiry-action-${inquiry.id}`}
            >
              {primaryAction.pulse && !primaryAction.disabled && (
                <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />
              )}
              {primaryAction.icon && <primaryAction.icon className="h-4 w-4 mr-2" />}
              {primaryAction.label}
            </Button>
          </div>
        )}

        {/* Footer: Sent date + Report (minimal) */}
        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
          <span className="text-xs text-gray-400">
            Sent {formatShortDate(inquiry.created_at)}
          </span>
          
          <div className="flex items-center gap-2">
            {inquiry.status === 'cancelled' && (() => {
              const existingDispute = disputes.find(d => d.inquiry_id === inquiry.id);
              return existingDispute ? (
                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">
                  Dispute {existingDispute.dispute_status}
                </Badge>
              ) : (
                <button
                  onClick={() => setDisputingInquiry(inquiry)}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Dispute
                </button>
              );
            })()}
            
            <button
              onClick={() => {
                navigate('/feedback', {
                  state: {
                    preSelectedInquiry: inquiry,
                    userRole: 'customer',
                    inquiries: inquiries
                  }
                });
              }}
              className="text-xs text-gray-400 hover:text-red-600"
            >
              Report
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InquiryCard;
